/** Zustand des Zahlungs-Schritts, aus dem sich seine Freigabe ergibt. */
export type PaymentStepState = {
  /** Ist das Zahlungsformular (Zahlart, Rhythmus, IBAN, Startdatum ...) gültig? */
  paymentFormValid: boolean;
  /** Wurde eine Online-Zahlart gewählt (PayPal, Google Pay, Buckaroo)? */
  isOnlinePayment: boolean;
  /** Ist für diese Online-Zahlart tatsächlich eine Zahlung zustande gekommen? */
  onlinePaymentSuccess: boolean;
};

/**
 * Der Zahlungs-Schritt gilt erst als erledigt, wenn das Formular gültig ist UND - bei einer
 * Online-Zahlart - auch wirklich bezahlt wurde.
 *
 * Ohne die zweite Bedingung meldet der Step sich bereits bei gültigem Formular als "completed".
 * Im linearen MatStepper ist genau dieses "completed" die Schranke, die den Header des
 * Folge-Schritts freigibt - der gesperrte "Weiter"-Button lässt sich dann per Klick auf diesen
 * Header umgehen und man landet ohne Zahlung auf der Zusammenfassung.
 *
 * Bei einer Offline-Zahlart (z.B. Lastschrift) genügt weiterhin das gültige Formular.
 */
export function isPaymentStepComplete(state: PaymentStepState): boolean {
  return state.paymentFormValid && (!state.isOnlinePayment || state.onlinePaymentSuccess);
}

/** Die per Konfiguration hinterlegten Zahlart-Codes der Online-Zahlungsanbieter. */
export type OnlinePaymentCodes = {
  /** paymentprovider.paypal.paymentcode */
  paypal?: string;
  /** paymentprovider.buckaroo.paymentcode */
  buckaroo?: string;
};

/**
 * Ist die gewählte Zahlart eine Online-Zahlart?
 *
 * Geprüft wird beides: der fachliche Typ aus dem Angebot UND die konfigurierten Zahlart-Codes der
 * Anbieter. Letzteres ist nötig, weil PayPal und Buckaroo im übrigen Code ausschließlich über
 * 'paymentprovider.<anbieter>.paymentcode' erkannt werden (s. handleTransaction) - verlässt man
 * sich allein auf den Typ, gilt die Zahlart bei der Auswahl noch als Offline-Zahlart und der
 * Zahlungs-Schritt gäbe sich frei, bevor überhaupt bezahlt wurde.
 */
export function isOnlinePaymentMethod(
  formOfPayment: {paymentType?: string, paymentMethod?: string},
  codes: OnlinePaymentCodes
): boolean {
  if (!formOfPayment) {
    return false;
  }
  if (formOfPayment.paymentType === 'ONLINEPAYMENT') {
    return true;
  }

  const paymentMethod = formOfPayment.paymentMethod;
  if (!paymentMethod) {
    return false;
  }
  return paymentMethod === codes?.paypal || paymentMethod === codes?.buckaroo;
}

/**
 * Darf vom Zahlungs-Schritt aus weitergesteppt werden - auch über den Stepper-Header?
 *
 * Zusätzlich zur Freigabe des Schritts muss der Nutzer den Schritt einmal abgeschlossen haben, also
 * die Preisermittlung angestoßen haben (priceConfirmed). Ohne diese Bedingung gilt der Schritt schon
 * als erledigt, sobald das Formular gültig ist - und das ist es bei Angeboten mit genau einer
 * Zahlart sofort, weil Zahlart, Rhythmus und Startdatum automatisch vorbelegt werden. Der Schritt
 * ließe sich dann überspringen, ohne ihn je angesehen zu haben (beobachtet bei ePaper-Angeboten).
 *
 * Bewusst getrennt von isPaymentStepComplete: Der "Weiter"-Button stößt die Preisermittlung erst an
 * und darf deshalb nicht von ihrem Ergebnis abhängen - sonst käme sie nie zustande.
 */
export function isPaymentStepNavigable(step: PaymentStepState, priceConfirmed: boolean): boolean {
  return isPaymentStepComplete(step) && !!priceConfirmed;
}
